import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Rocket, Monitor, AppWindow, Settings, MessageSquare, BarChart3, Menu as MenuIcon, X, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  getGuideIndex,
  getGuideContent,
  type GuideIndex,
  type GuideContentEnhanced,
  type GuideSectionMeta,
} from '../lib/api'
import { GuideTree } from '../components/content/GuideTree'
import { Breadcrumbs } from '../components/content/Breadcrumbs'
import { ContentNotFound } from '../components/content/ContentNotFound'
import { RichMarkdown } from '../components/content/RichMarkdown'
import { SearchBar } from '../components/content/SearchBar'
import { FeedbackButtons } from '../components/content/FeedbackButtons'
import { RelatedReleases } from '../components/content/RelatedReleases'
import { cn } from '../lib/utils'

const iconMap: Record<string, React.ElementType> = {
  Rocket,
  Monitor,
  AppWindow,
  Settings,
  MessageSquare,
  BarChart3,
}

/**
 * Recursively flatten section tree into a flat list of { slug, title }.
 */
function flattenSections(sections: GuideSectionMeta[]): { slug: string; title: string }[] {
  const result: { slug: string; title: string }[] = []
  for (const s of sections) {
    result.push({ slug: s.slug, title: s.title })
    if (s.children.length > 0) {
      result.push(...flattenSections(s.children))
    }
  }
  return result
}

function PrevNextNav({
  flatSections,
  currentSlug,
}: {
  flatSections: { slug: string; title: string }[]
  currentSlug: string
}) {
  const currentIndex = flatSections.findIndex((s) => s.slug === currentSlug)
  const prev = currentIndex > 0 ? flatSections[currentIndex - 1] : null
  const next = currentIndex < flatSections.length - 1 ? flatSections[currentIndex + 1] : null

  if (!prev && !next) return null

  return (
    <div className="flex justify-between items-center mt-8 pt-6 border-t border-border">
      {prev ? (
        <Link
          to={`/guide/${prev.slug}`}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          {prev.title}
        </Link>
      ) : (
        <div />
      )}
      {next ? (
        <Link
          to={`/guide/${next.slug}`}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {next.title}
          <ChevronRight className="w-4 h-4" />
        </Link>
      ) : (
        <div />
      )}
    </div>
  )
}

function GuideLanding({
  sections,
  onSearchSelect,
}: {
  sections: GuideSectionMeta[]
  onSearchSelect: (slug: string) => void
}) {
  // Find getting-started section for the callout
  const gettingStarted = sections.find((s) => s.slug === 'getting-started')
  const otherSections = sections.filter((s) => s.slug !== 'getting-started')

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">User Guide</h1>
      <p className="text-muted-foreground mb-6">
        Learn how to use Code Concierge -- from creating your first app to advanced deployment workflows.
      </p>

      {/* Search bar on landing page */}
      <div className="mb-8">
        <SearchBar onSelect={onSearchSelect} />
      </div>

      {/* Getting Started callout */}
      {gettingStarted && (
        <Link
          to={`/guide/${gettingStarted.slug}`}
          className="block mb-8 p-6 rounded-xl border-2 border-primary/30 bg-primary/5 hover:border-primary/50 transition-colors"
        >
          <div className="flex items-center gap-3 mb-2">
            <Rocket className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Getting Started</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            New to Code Concierge? Start here to set up your workspace and create your first app.
          </p>
        </Link>
      )}

      {/* Section cards grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {otherSections
          .sort((a, b) => a.order - b.order)
          .map((section) => {
            const Icon = section.icon ? iconMap[section.icon] : undefined
            const childCount = section.children.length
            return (
              <Link
                key={section.slug}
                to={`/guide/${section.slug}`}
                className="block p-5 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  {Icon && <Icon className="w-5 h-5 text-muted-foreground" />}
                  <h3 className="text-lg font-semibold">{section.title}</h3>
                </div>
                {childCount > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {childCount} {childCount === 1 ? 'section' : 'sections'}:{' '}
                    {section.children
                      .sort((a, b) => a.order - b.order)
                      .map((c) => c.title)
                      .join(', ')}
                  </p>
                )}
              </Link>
            )
          })}
      </div>
    </div>
  )
}

export default function Guide() {
  const { section } = useParams<{ section?: string }>()
  const navigate = useNavigate()

  const [guideIndex, setGuideIndex] = useState<GuideIndex | null>(null)
  const [content, setContent] = useState<GuideContentEnhanced | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mobileTreeOpen, setMobileTreeOpen] = useState(false)

  // Fetch guide index on mount
  useEffect(() => {
    getGuideIndex()
      .then((data) => setGuideIndex(data))
      .catch((err) => console.error('Failed to load guide index:', err))
  }, [])

  // Fetch section content when section changes
  useEffect(() => {
    if (!section) {
      setContent(null)
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    getGuideContent(section)
      .then((data) => {
        setContent(data)
        setError(null)
      })
      .catch((err) => {
        const msg = err.message || ''
        if (msg.toLowerCase().includes('not found') || msg.includes('404')) {
          setError('not_found')
        } else {
          setError(msg || 'Failed to load content')
        }
        setContent(null)
      })
      .finally(() => setLoading(false))
  }, [section])

  const flatSections = useMemo(
    () => (guideIndex ? flattenSections(guideIndex.sections) : []),
    [guideIndex]
  )

  const handleSelect = useCallback(
    (slug: string) => {
      navigate(`/guide/${slug}`)
      setMobileTreeOpen(false)
    },
    [navigate]
  )

  return (
    <div className="flex h-full -m-4 sm:-m-8">
      {/* Mobile tree overlay */}
      {mobileTreeOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileTreeOpen(false)}
        />
      )}

      {/* Guide tree sidebar */}
      <aside
        className={cn(
          'w-64 shrink-0 border-r border-border p-4 bg-card z-50 md:sticky md:top-0 md:h-screen md:overflow-y-auto',
          // Desktop: always visible
          'hidden md:block',
          // Mobile: overlay when open
          mobileTreeOpen && 'fixed inset-y-0 left-0 block md:block'
        )}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Guide
          </h2>
          {mobileTreeOpen && (
            <button
              onClick={() => setMobileTreeOpen(false)}
              className="p-1 rounded hover:bg-secondary transition-colors md:hidden"
              aria-label="Close navigation"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {guideIndex ? (
          <GuideTree
            sections={guideIndex.sections}
            activeSlug={section || ''}
            onSelect={handleSelect}
          />
        ) : null}
      </aside>

      {/* Content area */}
      <main className="flex-1 overflow-y-auto p-6 max-w-4xl">
        {/* Mobile tree toggle */}
        <button
          onClick={() => setMobileTreeOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary text-sm mb-4 md:hidden"
        >
          <MenuIcon className="w-4 h-4" />
          Guide navigation
        </button>

        {section ? (
          // Section view: search + breadcrumbs + content + related + feedback
          <>
            <div className="mb-4">
              <SearchBar onSelect={handleSelect} />
            </div>
            <Breadcrumbs crumbs={content?.breadcrumbs || []} />
            {loading ? null : error ? (
              <ContentNotFound
                type="guide"
                slug={section}
                allSections={flatSections}
              />
            ) : content ? (
              <div>
                <RichMarkdown content={content.content} />
                <PrevNextNav flatSections={flatSections} currentSlug={section!} />
                <RelatedReleases releases={content.related_releases || []} />
                <FeedbackButtons contentType="guide" slug={section} />
              </div>
            ) : null}
          </>
        ) : (
          // Landing page: section cards
          <GuideLanding sections={guideIndex?.sections || []} onSearchSelect={handleSelect} />
        )}
      </main>
    </div>
  )
}
